import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTheme } from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import AppBar from '@mui/material/AppBar';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import logoBuildingControl from '../logoBuildingControl.png';
import { VarName, varNameDetails } from '../utils/varNames';
import { varNameBandParams } from '../utils/dataBandParams';
import {
  isHistoryItem,
  isMetricItem,
  SensorHistoryPlotItem,
  SensorMetricPlotItem,
  TimeRange,
} from '../Widgets/Plots/plotCommon';
import varNameDescriptions, { BandParamDescription } from './varNameDescriptions';

import useStyles from '../styles/report';
import '../utils/print.css';
import {
  getCurrentLocation,
  getLocationsById,
  getSelectedSensorIds,
  getSensorsById,
} from '../state/selectors';
import { SensorStatsSummary, calculateStats, calculateMetricStats } from '../utils/statistics';
import { useAppSelector } from '../state/store';
import StackedHistoryPlot from '../Widgets/Plots/StackedHistoryPlot';
import StackedMetricPlot from '../Widgets/Plots/StackedMetricPlot';
import AnalysisSection from '../Widgets/Compare/AnalysisSection';
import { getCommonLocId, MetricOptions } from '../Widgets/Compare/helpers';

dayjs.extend(advancedFormat);

export interface ComparePageReportProps {
  sensorData: SensorHistoryPlotItem[] | SensorMetricPlotItem[];
  stats: Map<VarName, SensorStatsSummary[]>;
  dateRange: [Date, Date];
  selectedHours: [number, number];
  includeWeekends: boolean;
  onClose: () => void;
  fetchingSensorData: boolean;
  unselectedTimes: TimeRange[] | undefined;
  metricSelection: MetricOptions;
}

export function statsPerVarNamePerSensor(
  data: SensorHistoryPlotItem[] | SensorMetricPlotItem[],
  selectedHours: [number, number],
  includeWeekends: boolean
) {
  const stats = new Map<VarName, SensorStatsSummary[]>();
  data.forEach((item) => {
    if ((isHistoryItem(item) && item.history.time.length > 0) || isMetricItem(item)) {
      let sensors = stats.get(item.varName);
      const sensorStats = isHistoryItem(item)
        ? calculateStats(item.varName, item.history, selectedHours, includeWeekends)
        : calculateMetricStats(item.varName, item.metrics, selectedHours, includeWeekends);
      const sensor = {
        sensorId: item.sensorId,
        sensorName: item.sensorName,
        stats: sensorStats,
      };
      if (!sensors) {
        sensors = [sensor];
        stats.set(item.varName, sensors);
      } else {
        sensors.push(sensor);
      }
    }
  });

  return stats;
}

function ComparePageReport({
  sensorData,
  stats,
  dateRange,
  selectedHours,
  includeWeekends,
  onClose,
  fetchingSensorData,
  unselectedTimes,
  metricSelection,
}: ComparePageReportProps): JSX.Element {
  const { classes } = useStyles();
  const theme = useTheme();
  const currentLocation = useAppSelector(getCurrentLocation);
  const locationsById = useAppSelector(getLocationsById);
  const sensorsById = useAppSelector(getSensorsById);
  const selectedSensorIds = useAppSelector(getSelectedSensorIds);
  const [showMinMaxAvg, setShowMinMaxAvg] = useState<boolean>(false);

  const originalTitle = useRef<string | null>(null);

  const print = () => {
    window.print();
  };

  // we aim to find common location id based on selected sensors
  const commonLocId = useMemo((): string => {
    const locId = getCommonLocId(selectedSensorIds, sensorsById);
    return locId;
  }, [selectedSensorIds, sensorsById]);

  const locFullName = useMemo(() => {
    let loc = commonLocId;
    const propLocItem = locationsById.get(commonLocId ?? '');
    const locName = propLocItem?.name;
    if (commonLocId && commonLocId === '#') loc = locationsById.get('#')?.name ?? '';
    else if (commonLocId && commonLocId.startsWith('#')) {
      const parentLocName = locationsById.get(propLocItem?.location ?? '')?.name;
      loc = `${parentLocName}, ${locName}`;
    }

    return loc ?? '';
  }, [commonLocId, locationsById]);

  // change print title when printing report
  useEffect(() => {
    const handleBeforePrint = () => {
      originalTitle.current = document.title;
      document.title = `Compare Report for ${locFullName ?? currentLocation}`;
    };

    const handleAfterPrint = () => {
      if (originalTitle.current) {
        document.title = originalTitle.current;
      }
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ***********************************************************************************
  // NOTE: It is IMPORTANT that we only use HTML elements within the <main> content
  // so that we can apply the CSS print styles effectively when the user wants to print.
  // Using Typography from Material or other such components will complicate things.
  // ***********************************************************************************
  return (
    <Box>
      <div className={`${classes.compareReport} print`}>
        <AppBar className={`${classes.header} no-print`}>
          <Toolbar>
            <div style={{ flex: '1 1 auto' }} />
            {fetchingSensorData && <CircularProgress style={{ color: 'white' }} size={20} />}
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={showMinMaxAvg}
                  onChange={() => setShowMinMaxAvg(!showMinMaxAvg)}
                />
              }
              label="Show Min/Max/Avg"
            />
            <Button onClick={print} className={classes.headerButton} startIcon={<PrintIcon />}>
              Print
            </Button>
            <Button onClick={onClose} className={classes.headerButton} startIcon={<CloseIcon />}>
              Close
            </Button>
          </Toolbar>
        </AppBar>
        <main className={classes.main}>
          <div className={classes.page}>
            <div className={classes.reportLogo} style={{ textAlign: 'end' }}>
              <img src={logoBuildingControl} alt="logo" style={{ width: '120px' }} />
            </div>
            <h2 className={classes.reportTitle}>Report for {locFullName ?? currentLocation}</h2>
            Reporting for period {dayjs(dateRange[0]).format('dddd MMMM Do YYYY')}, 12:00 am to{' '}
            {dayjs(dateRange[1]).format('dddd MMMM Do YYYY')}, 11:59pm.
            <p>
              Hours analysed are between {selectedHours[0]}:00 and {selectedHours[1]}:00. Weekends{' '}
              <b>are {!includeWeekends && 'not '}</b>included in the analysis.
            </p>
            <Box>
              {metricSelection === MetricOptions.Raw ? (
                <StackedHistoryPlot
                  dataItems={sensorData as SensorHistoryPlotItem[]}
                  unselectedTimes={unselectedTimes}
                />
              ) : (
                <StackedMetricPlot
                  dataItems={sensorData as SensorMetricPlotItem[]}
                  unselectedTimes={unselectedTimes}
                  metricSelection={metricSelection}
                />
              )}
            </Box>
          </div>
          {Array.from(stats.keys())
            .sort((a, b) => {
              const pa = varNameDetails[a].reportSortOrder ?? Infinity;
              const pb = varNameDetails[b].reportSortOrder ?? Infinity;
              if (pa < pb) return -1;
              if (pa > pb) return 1;

              const la = varNameDetails[a].label;
              const lb = varNameDetails[b].label;
              if (la < lb) return -1;
              if (la > lb) return 1;

              return 0;
            })
            .map((varName) => (
              <section key={`reportSection-${varName}`} className={classes.page}>
                <h1>{varNameDetails[varName].label}</h1>
                {varNameDescriptions[varName].description}
                {varNameBandParams[varName] && <BandParamDescription varName={varName} />}

                <AnalysisSection
                  varName={varName}
                  statsPerSensor={stats.get(varName)}
                  showMinMaxAvg={showMinMaxAvg}
                />

                {varNameDescriptions[varName].footnotes && (
                  <aside>
                    <h3>References</h3>
                    <ol>
                      {varNameDescriptions[varName].footnotes?.map((footnote) => (
                        <li key={`reportSection-reference-${footnote.id}`}>
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href={footnote.url}
                            style={{ color: theme.palette.text.primary }}
                          >
                            {footnote.text}
                          </a>
                          ,
                        </li>
                      ))}
                    </ol>
                  </aside>
                )}
              </section>
            ))}
        </main>
      </div>
    </Box>
  );
}

export default ComparePageReport;
